import React, { forwardRef, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { FaRegCalendarAlt } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import './OpenFormDatePicker.css';

const pad = (value) => String(value).padStart(2, '0');

const parseDateValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;

  const [year, month, day] = text.split('-').map(Number);
  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateTimeValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;

  const reportTimestampMatch = text.match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(AM|PM)$/i
  );
  if (reportTimestampMatch) {
    const [, day, month, year, hour, minutes, seconds = '0', meridiem] = reportTimestampMatch;
    let hours = Number(hour) % 12;
    if (meridiem.toUpperCase() === 'PM') hours += 12;

    const parsedReportTimestamp = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours,
      Number(minutes),
      Number(seconds)
    );
    return Number.isNaN(parsedReportTimestamp.getTime()) ? null : parsedReportTimestamp;
  }

  const [datePart = '', timePart = ''] = text.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours = 0, minutes = 0] = timePart.split(':').map(Number);

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day, hours, minutes);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateValue = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
);

const formatDateTimeValue = (date) => (
  `${formatDateValue(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);

const isSameDay = (left, right) => (
  Boolean(left)
  && Boolean(right)
  && left.getFullYear() === right.getFullYear()
  && left.getMonth() === right.getMonth()
  && left.getDate() === right.getDate()
);

const getDayBoundaryTime = (date, hours, minutes) => {
  const nextDate = new Date(date);
  nextDate.setHours(hours, minutes, 0, 0);
  return nextDate;
};

const PickerInput = forwardRef(function PickerInput(
  { id, value, onClick, disabled, readOnly, error },
  ref
) {
  const allowOpenRef = useRef(false);
  const handlePointerOpen = (event) => {
    if (disabled || readOnly) {
      return;
    }

    if (allowOpenRef.current) {
      onClick?.(event);
    }

    allowOpenRef.current = false;
  };

  return (
    <div
      className={`open-form-datepicker-input-shell${error ? ' is-error' : ''}${disabled ? ' is-disabled' : ''}`}
      onMouseDown={() => {
        allowOpenRef.current = true;
      }}
      onClick={handlePointerOpen}
      onBlur={() => {
        allowOpenRef.current = false;
      }}
    >
      <input
        id={id}
        ref={ref}
        type="text"
        value={value}
        readOnly
        disabled={disabled}
        aria-invalid={error}
        className="open-form-datepicker-input"
        autoComplete="off"
        tabIndex={readOnly ? -1 : 0}
        onKeyDown={(event) => {
          if (disabled || readOnly) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            onClick?.(event);
          }
        }}
      />
      <span className="open-form-datepicker-icon" aria-hidden="true">
        <FaRegCalendarAlt />
      </span>
    </div>
  );
});

export default function OpenFormDatePicker({
  id,
  name,
  type = 'date',
  value,
  onChange,
  min,
  max,
  error = false,
  disabled = false,
  readOnly = false,
}) {
  const isDateTime = type === 'datetime';
  const [open, setOpen] = useState(false);
  const pickerValue = useMemo(
    () => (isDateTime ? parseDateTimeValue(value) : parseDateValue(value)),
    [isDateTime, value]
  );
  const minValue = useMemo(
    () => (isDateTime ? parseDateTimeValue(min) : parseDateValue(min)),
    [isDateTime, min]
  );
  const maxValue = useMemo(
    () => (isDateTime ? parseDateTimeValue(max) : parseDateValue(max)),
    [isDateTime, max]
  );

  const minTime = useMemo(() => {
    if (!isDateTime || !pickerValue || !minValue || !isSameDay(pickerValue, minValue)) {
      return undefined;
    }

    return minValue;
  }, [isDateTime, minValue, pickerValue]);

  const maxTime = useMemo(() => {
    if (!isDateTime || !pickerValue || !maxValue || !isSameDay(pickerValue, maxValue)) {
      return undefined;
    }

    return maxValue;
  }, [isDateTime, maxValue, pickerValue]);

  const effectiveMinTime = useMemo(() => {
    if (!isDateTime) return undefined;
    return minTime || getDayBoundaryTime(pickerValue || new Date(), 0, 0);
  }, [isDateTime, minTime, pickerValue]);

  const effectiveMaxTime = useMemo(() => {
    if (!isDateTime) return undefined;
    return maxTime || getDayBoundaryTime(pickerValue || new Date(), 23, 59);
  }, [isDateTime, maxTime, pickerValue]);

  const handlePickerChange = (nextValue) => {
    const nextTextValue = !nextValue
      ? ''
      : isDateTime
        ? formatDateTimeValue(nextValue)
        : formatDateValue(nextValue);

    onChange({
      target: {
        name,
        value: nextTextValue,
        type: 'text',
        checked: false,
      },
    });
  };

  const handleOpen = () => {
    if (!disabled && !readOnly) {
      setOpen(true);
    }
  };

  return (
    <DatePicker
      selected={pickerValue}
      onChange={handlePickerChange}
      onClickOutside={() => setOpen(false)}
      onCalendarClose={() => setOpen(false)}
      onCalendarOpen={() => setOpen(true)}
      open={open}
      onInputClick={handleOpen}
      disabled={disabled}
      readOnly={readOnly}
      minDate={minValue}
      maxDate={maxValue}
      minTime={effectiveMinTime}
      maxTime={effectiveMaxTime}
      showTimeSelect={isDateTime}
      showTimeSelectOnly={false}
      timeIntervals={1}
      timeCaption="Time"
      dateFormat={isDateTime ? 'MM/dd/yyyy hh:mm aa' : 'MM/dd/yyyy'}
      placeholderText={isDateTime ? 'mm/dd/yyyy hh:mm am' : 'mm/dd/yyyy'}
      wrapperClassName="open-form-datepicker-wrapper"
      popperClassName="open-form-datepicker-popper"
      calendarClassName="open-form-datepicker-calendar"
      shouldCloseOnSelect={!isDateTime}
      customInput={(
        <PickerInput
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          error={error}
        />
      )}
      {...(isDateTime
        ? {}
        : {
            showPopperArrow: false,
          })}
    />
  );
}
